<template>
  <div class="chart-component-container position-relative">
    <b-icon-fullscreen
      v-b-tooltip.hover
      class="clickable chart-button"
      variant="primary"
      @click="enlargeChart = true"
      title="Focus Mode"
    />
    <div class="chart-enlarge" v-if="enlargeChart">
      <b-button
        variant="outline-primary"
        size="sm"
        @click="enlargeChart = false"
      >
        <b-icon-chevron-left />
        back
      </b-button>
      <chart-viewer
        :type="parsedChartSettings.chartType"
        :data="chartData"
        :options="chartOptions"
        :is-enlarge="true"
      />
    </div>
    <chart-viewer
      :type="parsedChartSettings.chartType"
      :data="chartData"
      :options="chartOptions"
    />
  </div>
</template>

<script>
/*global _*/
import application from '@/mixins/application';
import ChartViewer from '@/components/charts/ChartViewer';
import numeral from 'numeral';

export default {
  name: 'ChartComponent',
  mixins: [application],
  components: { ChartViewer },
  props: {
    chartSettings: Object,
  },
  data() {
    return {
      dataset: null,
      parsedChartSettings: { chartType: '', options: {} },

      selectedChartType: '',

      enlargeChart: false,
    };
  },
  mounted() {
    const vm = this;
    this.loadDataset().then(() => {
      if (vm.dataset) {
        this.parseChartSettings();
      }
    });
  },
  watch: {
    chartSettings(newValue) {
      //parse the stringify chart settings to JSON format
      const vm = this;
      if (newValue.reportDatasetId === this.dataset.id) {
        this.parseChartSettings();
      } else {
        this.loadDataset().then(() => {
          if (vm.dataset) {
            this.parseChartSettings();
          }
        });
      }
    },
  },
  computed: {
    chartOptions() {
      const options = {};
      options.plugins = {};
      const chartSettings = this.chartSettings;
      const title = chartSettings.title;
      const axisFormat = chartSettings.axisFormat;
      const valueFormat = chartSettings.valueFormat;
      const axis = chartSettings.axis || 'y';

      if (title) {
        options.plugins.title = { display: true, text: title };
      }

      if (axisFormat) {
        options.scales = {};
        options.scales[axis] = {
          beginAtZero: true,
          ticks: {
            callback: function(value) {
              return numeral(value).format(axisFormat);
            },
          },
        };
      }

      if (valueFormat) {
        options.plugins.tooltip = {
          callbacks: {
            label: function(context) {
              let value = context.parsed[axis] || '';
              if (value) {
                return numeral(value).format(valueFormat);
              }
              return value;
            },
          },
        };
      }

      options.indexAxis = axis === 'y' ? 'x' : 'y';

      return options;
    },
    dimensionFields() {
      return this.dataset.datasetFields.filter(
        field => field.class === 'smartgrabber-dimension'
      );
    },
    measureFields() {
      return this.dataset.datasetFields.filter(
        field => field.class === 'smartgrabber-measure'
      );
    },
    chartData() {
      if (
        this.dataset &&
        this.parsedChartSettings.chartType &&
        this.measureFields.length > 0
      ) {
        try {
          const chartSettings = _.cloneDeep(this.parsedChartSettings);
          const rows = _.cloneDeep(this.dataset.datasetCachedData);
          const measureFields = _.cloneDeep(this.measureFields);
          const chartData = {};
          const dimensionKey = chartSettings.selectedDimensions[0].dimensionKey;
          const axis = chartSettings.axis || 'y';
          const orderBy = chartSettings.orderBy || [null];
          const orderDirection = chartSettings.orderDirection;

          let aggData = aggSum(dimensionKey, measureFields, rows, axis);

          if (orderBy[0]) {
            const field = _.find(this.dataset.datasetFields, {
              key: orderBy[0],
            });
            const order = [];
            if (field.class === 'smartgrabber-dimension') {
              order.push(axis === 'y' ? 'x' : 'y');
            } else {
              order.push(field.label);
            }
            aggData = _.orderBy(aggData, order, orderDirection);
          }

          chartData.datasets = [];
          _.each(chartSettings.selectedMeasures, selectedMeasure => {
            const meaName = _.find(measureFields, {
              key: selectedMeasure.measureKey,
            }).label;
            let measureData = {
              label: meaName,
              data: aggData,
              parsing: {},
            };
            measureData.parsing[axis + 'AxisKey'] = meaName;
            if (selectedMeasure.backgroundColor) {
              const chartType = this.parsedChartSettings.chartType;
              if (chartType === 'line') {
                measureData.backgroundColor = selectedMeasure.backgroundColor;
                measureData.borderColor =
                  selectedMeasure.backgroundColor + '99';
              } else {
                measureData.backgroundColor =
                  selectedMeasure.backgroundColor + '99';
                measureData.borderColor = selectedMeasure.backgroundColor;
              }
            }
            chartData.datasets.push(measureData);
          });
          chartData.options = {
            scales: {},
          };
          chartData.options.scales[this.axis] = { beginAtZero: true };
          return chartData;
        } catch (ex) {
          // eslint-disable-next-line no-console
          console.log(ex);
          return {};
        }
      } else {
        return {};
      }
    },
  },
  methods: {
    parseChartSettings() {
      const chartSettings = _.cloneDeep(this.chartSettings);
      chartSettings.selectedDimensions = JSON.parse(
        chartSettings.selectedDimensions
      );
      chartSettings.selectedMeasures = JSON.parse(
        chartSettings.selectedMeasures
      );
      _.each(chartSettings.selectedMeasures, measure => {
        if (!measure.backgroundColor) {
          measure.backgroundColor = '#ABB8C3';
        }
      });
      chartSettings.options = JSON.parse(chartSettings.options);
      chartSettings.orderBy = JSON.parse(chartSettings.orderBy);
      chartSettings.orderDirection = JSON.parse(chartSettings.orderDirection);
      this.parsedChartSettings = chartSettings;
    },
    loadDataset() {
      const vm = this;
      return vm.axios
        .get('/api/datasets/' + vm.chartSettings.reportDatasetId)
        .then(resp => {
          if (resp.data) {
            const dataset = _.cloneDeep(resp.data);
            dataset.datasetCachedData = JSON.parse(dataset.datasetCachedData);
            dataset.datasetFields = JSON.parse(dataset.datasetFields);
            vm.dataset = dataset;
          } else vm.dataset = null;
        })
        .catch(() => {
          vm.PromptWarning(
            'Failed',
            'Failed to load the data. Please try again.'
          );
        });
    },
  },
};

function aggSum(dimKey, measureFields, rows, axis) {
  const dataAxis = axis === 'y' ? 'x' : 'y';
  return _.values(
    _.reduce(
      rows,
      (result, row) => {
        const rowData = {};

        const dimVal = row[dimKey];
        rowData[dataAxis] = dimVal;
        _.each(measureFields, measureField => {
          const meaKey = measureField.key;
          const meaName = measureField.label;
          const meaVal = row[meaKey];
          rowData[meaName] =
            parseFloat(meaVal) + (result[dimVal] ? result[dimVal][meaName] : 0);
        });
        result[dimVal] = rowData;

        return result;
      },
      {}
    )
  );
}
</script>

<style scoped>
.chart-enlarge {
  position: fixed;
  padding: 3em;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  background-color: #fefefe;
  z-index: 100;
}

.chart-button {
  position: absolute;
  top: -0.8em;
  right: -0.8em;
  height: 0.8em;
}
</style>